import { useState } from 'react';
import { X, AudioLines } from 'lucide-react';
import { useVoice } from '../contexts/VoiceContext';
import { AudioInputPicker } from './AudioInputPicker';
import './VoiceStartDialog.css';

// Pre-start config for a Realtime voice session (Alex 2026-06-26): mode + detail are
// frozen at mint, so let the operator confirm/change them right before Start instead
// of digging in Settings. Defaults come from localStorage (the last choice); changes
// persist immediately, so "Start" with no change just uses the standing default.

function get(key: string, fallback: string): string {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}

export function VoiceStartDialog({ onStart, onClose }: { onStart: () => void; onClose: () => void }) {
  const voice = useVoice();
  const [mode, setMode] = useState(() => localStorage.getItem('cloud-voice-mode') || (localStorage.getItem('cloud-voice-talkback') === 'on' ? 'talkback' : 'narrator'));
  const [detail, setDetail] = useState(() => get('cloud-voice-detail', 'balanced'));
  const [ambient, setAmbient] = useState(() => get('cloud-voice-ambient', 'focus') === 'ambient');
  const [bg, setBg] = useState(() => get('cloud-voice-bg', 'off') === 'on');

  const changeMode = (m: string) => {
    setMode(m);
    localStorage.setItem('cloud-voice-mode', m);
    // Keep the legacy talkback flag in sync for VoiceContext's mint param.
    localStorage.setItem('cloud-voice-talkback', m === 'talkback' ? 'on' : 'off');
    window.dispatchEvent(new CustomEvent('cloud-voice-talkback-change'));
  };
  const changeDetail = (v: string) => { setDetail(v); localStorage.setItem('cloud-voice-detail', v); };
  const toggleAmbient = () => {
    const next = !ambient; setAmbient(next);
    const v = next ? 'ambient' : 'focus';
    localStorage.setItem('cloud-voice-ambient', v);
    window.dispatchEvent(new CustomEvent('cloud-voice-ambient-change', { detail: v }));
  };
  const toggleBg = () => { const next = !bg; setBg(next); localStorage.setItem('cloud-voice-bg', next ? 'on' : 'off'); };

  return (
    <div className="vsd-backdrop" onClick={onClose}>
      <div className="vsd-modal" onClick={(e) => e.stopPropagation()}>
        <header className="vsd-header">
          <h2><AudioLines size={14} /> Realtime starten</h2>
          <button className="vsd-close" onClick={onClose}><X size={16} /></button>
        </header>

        <div className="vsd-body">
          <label className="vsd-label">
            Modus
            <select className="vsd-select" value={mode} onChange={(e) => changeMode(e.target.value)}>
              <option value="narrator">Narrator (3. Person)</option>
              <option value="talkback">Talkback</option>
              <option value="transparent">Gespräch (transparent)</option>
              <option value="arcturian">Arcturian</option>
            </select>
          </label>

          <label className="vsd-label">
            Detailgrad
            <select className="vsd-select" value={detail} onChange={(e) => changeDetail(e.target.value)}>
              <option value="brief">Knapp</option>
              <option value="balanced">Ausgewogen</option>
              <option value="flowing">Flüssig</option>
              <option value="technical">Technisch</option>
            </select>
          </label>

          <label className="vsd-label">
            Mikrofoneingang
            <AudioInputPicker
              devices={voice.audioInputs}
              selectedId={voice.selectedAudioInputId}
              activeLabel=""
              switching={voice.audioInputSwitching}
              error={voice.audioInputError}
              onChange={(deviceId) => { void voice.selectAudioInput(deviceId); }}
            />
            {voice.audioInputs.length === 0 && (
              <button
                type="button"
                className="vsd-audio-scan"
                disabled={voice.audioInputSwitching}
                onClick={() => { void voice.requestAudioInputAccess(); }}
              >
                {voice.audioInputSwitching ? 'Mikrofone werden gelesen…' : 'Mikrofone anzeigen'}
              </button>
            )}
          </label>

          <label className="vsd-check">
            <input type="checkbox" checked={ambient} onChange={toggleAmbient} />
            <span>Ambient — wandert bei Idle zu aktiven Agenten</span>
          </label>
          <label className="vsd-check">
            <input type="checkbox" checked={bg} onChange={toggleBg} />
            <span>Hintergrund-Arbeit miterzählen (Tools/Prozess-Schritte)</span>
          </label>
        </div>

        <div className="vsd-actions">
          <button className="vsd-cancel" onClick={onClose}>Abbrechen</button>
          <button className="vsd-start" onClick={() => { onStart(); onClose(); }}>Start</button>
        </div>
      </div>
    </div>
  );
}
